// NOTE: USING INLINE STYLES BECAUSE TAILWIND CSS DOES NOT SUPPORT DYNAMIC VALUES FROM PROPS. THE FOLLOWING LINK EXPLAINS WHY:
// https://v2.tailwindcss.com/docs/just-in-time-mode#arbitrary-value-support

import { useEffect, useState, useRef, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { TeleprompterProps, Chart } from '@usacsmideo/types';
import useChart from '../../hooks/useChart';
import { useAuth } from '../../contexts/AuthContext';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import * as Sentry from '@sentry/browser';

export const TeleprompterText = (props: TeleprompterProps) => {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const data = chartQuery.data as Chart;
  const deviceDetection = useDeviceDetection();
  const [scriptHeight, setScriptHeight] = useState(0);

  // Manual scroll state
  const [manualScrollPosition, setManualScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [scrollStartPosition, setScrollStartPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const keyHoldTimeoutRef = useRef<NodeJS.Timeout>();
  const keyHoldIntervalRef = useRef<NodeJS.Timeout>();

  // Parse script into slides for slide-advance mode
  const slides = useMemo(() => {
    if (!data?.SCRIPT_REVIEW?.telePrompterScript) return [];
    return data.SCRIPT_REVIEW.telePrompterScript
      .split('\n')
      .filter((line) => line.trim().length > 0);
  }, [data?.SCRIPT_REVIEW?.telePrompterScript]);

  const style: CSSProperties = {
    fontSize: `${props.textSize}px`,
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
    padding: '10px',
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
  };

  // Adjust script height for auto-scroll and manual-scroll modes
  useEffect(() => {
    if (props.mode === 'slide-advance') return;

    const adjustHeight = () => {
      const script = document.getElementById('script-container');
      if (script) {
        const scriptDiv = script.getBoundingClientRect();
        setScriptHeight(scriptDiv.height);
      } else {
        Sentry.captureMessage('No div containing script text yet');
        console.error('No div containing script text yet');
      }
    };

    // Initial height calculation
    adjustHeight();

    // Recalculate on window resize
    window.addEventListener('resize', adjustHeight);

    return () => {
      window.removeEventListener('resize', adjustHeight);
    };
  }, [props.textSize, props.mode]);

  // Reset manual scroll position when mode changes
  useEffect(() => {
    setManualScrollPosition(0);
    if (keyHoldTimeoutRef.current) {
      clearTimeout(keyHoldTimeoutRef.current);
    }
    if (keyHoldIntervalRef.current) {
      clearInterval(keyHoldIntervalRef.current);
    }
  }, [props.mode]);

  // Listen for mobile scroll events (for manual-scroll mode on mobile)
  useEffect(() => {
    if (props.mode !== 'manual-scroll') return;

    const handleMobileScroll = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { direction, action } = customEvent.detail;

      if (action === 'start') {
        // Single tap: 50px discrete step
        setManualScrollPosition((prev) => {
          const maxScroll = scriptHeight * 1.5;
          if (direction === 'up') {
            return Math.min(0, prev + 50);
          } else {
            return Math.max(-maxScroll, prev - 50);
          }
        });
      } else if (action === 'continue') {
        // Holding: smooth continuous scroll using speed
        const pixelsPerFrame = (scriptHeight * 1.5) / props.speed / 60;
        setManualScrollPosition((prev) => {
          const maxScroll = scriptHeight * 1.5;
          const delta = direction === 'up' ? pixelsPerFrame : -pixelsPerFrame;
          return Math.max(-maxScroll, Math.min(0, prev + delta));
        });
      }
    };

    window.addEventListener('mobileScroll', handleMobileScroll);

    return () => {
      window.removeEventListener('mobileScroll', handleMobileScroll);
    };
  }, [props.mode, props.speed, scriptHeight]);

  // Mouse/Touch drag handlers for manual-scroll
  const handleDragStart = (clientY: number) => {
    if (props.mode !== 'manual-scroll') return;
    setIsDragging(true);
    setDragStartY(clientY);
    setScrollStartPosition(manualScrollPosition);
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging || props.mode !== 'manual-scroll') return;
    const delta = clientY - dragStartY;
    const newPosition = scrollStartPosition + delta;

    // Clamp scroll position to prevent scrolling beyond bounds
    const maxScroll = scriptHeight * 1.5;
    const clampedPosition = Math.max(-maxScroll, Math.min(0, newPosition));
    setManualScrollPosition(clampedPosition);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Note: Keyboard scrolling now uses the same custom event system as buttons
  // No separate animation loop needed

  // Keyboard event handlers (desktop only)
  useEffect(() => {
    if (!deviceDetection.isDesktop) return;

    // For auto-scroll, only enable during scrolling
    if (props.mode === 'auto-scroll' && !props.isScrolling) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Manual-scroll mode: Up/Down arrows
      if (props.mode === 'manual-scroll') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();

          // Prevent repeated keydown events when holding key
          if (e.repeat) return;

          const direction = e.key === 'ArrowUp' ? 'up' : 'down';

          // Trigger through custom event system (same as buttons)
          window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { direction, action: 'start' } }));

          // Start continuous scrolling after delay (same as buttons)
          keyHoldTimeoutRef.current = setTimeout(() => {
            keyHoldIntervalRef.current = setInterval(() => {
              window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { direction, action: 'continue' } }));
            }, 50);
          }, 300);
        }
      }

      // Slide-advance mode: Left/Right arrows
      if (props.mode === 'slide-advance') {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          if (props.onSlideChange && props.currentSlide !== undefined && props.currentSlide > 0) {
            props.onSlideChange(props.currentSlide - 1);
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          if (
            props.onSlideChange &&
            props.currentSlide !== undefined &&
            props.totalSlides !== undefined &&
            props.currentSlide < props.totalSlides - 1
          ) {
            props.onSlideChange(props.currentSlide + 1);
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (props.mode === 'manual-scroll' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        // Clear the hold timeout and interval
        if (keyHoldTimeoutRef.current) {
          clearTimeout(keyHoldTimeoutRef.current);
        }
        if (keyHoldIntervalRef.current) {
          clearInterval(keyHoldIntervalRef.current);
        }
        window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { action: 'stop' } }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    deviceDetection.isDesktop,
    props.mode,
    props.isScrolling,
    props.currentSlide,
    props.totalSlides,
    props.onSlideChange,
    scriptHeight,
  ]);

  if (data) {
    // Auto-scroll mode (original behavior)
    if (props.mode === 'auto-scroll') {
      return (
        <>
          <div style={style}>
            <div
              style={
                props.isScrolling
                  ? {
                      position: 'absolute',
                      transition: `top ${props.speed}s`,
                      transitionTimingFunction: 'linear',
                      width: '100%',
                      top: scriptHeight ? -scriptHeight * 1.5 : -1000,
                    }
                  : {
                      transition: 'top 0s',
                      top: '50%',
                      width: '100%',
                    }
              }
            >
              <div id="script-container">
                <p id="script" style={{ whiteSpace: 'pre-wrap' }}>
                  {data.SCRIPT_REVIEW.telePrompterScript}
                </p>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Manual-scroll mode
    if (props.mode === 'manual-scroll') {
      return (
        <>
          <div
            ref={containerRef}
            style={{
              ...style,
              pointerEvents: 'auto',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              touchAction: 'none',
              height: '100%',
              overflow: 'hidden',
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleDragStart(e.clientY);
            }}
            onMouseMove={(e) => handleDragMove(e.clientY)}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDragStart(e.touches[0].clientY);
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDragMove(e.touches[0].clientY);
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleDragEnd();
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                top: manualScrollPosition,
                transition: isDragging ? 'none' : 'top 0.1s ease-out',
              }}
            >
              <div id="script-container">
                <p id="script" style={{ whiteSpace: 'pre-wrap' }}>
                  {data.SCRIPT_REVIEW.telePrompterScript}
                </p>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Slide-advance mode
    if (props.mode === 'slide-advance') {
      const currentSlideText = props.currentSlide !== undefined ? slides[props.currentSlide] || '' : slides[0] || '';

      return (
        <>
          <div
            style={{
              ...style,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '100%',
                animation: 'fadeIn 0.3s ease-in',
              }}
            >
              <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{currentSlideText}</p>
            </div>
          </div>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>
        </>
      );
    }
  }

  return <div>Loading...</div>;
};
